import { useCallback, useEffect, useState } from "react";
import type { BobState, SurfaceId } from "../types";
import { buildSovereignStatus } from "../lib/engine/exportCenter";
import { seedFailureCards } from "../lib/engine/failures";
import {
  seedAgentCards,
  seedDemoDirectives,
  syncAgentsWithDirectives,
} from "../lib/engine/pipeline";
import { loadState, saveState } from "../lib/storage";

function defaultState(): BobState {
  const directives = seedDemoDirectives();
  return {
    intakes: [],
    graderResults: [],
    prompts: [],
    artifactPlans: [],
    mergePlans: [],
    directives,
    agents: syncAgentsWithDirectives(seedAgentCards(), directives),
    failures: seedFailureCards(),
    activeSurface: "deck",
    presentationMode: false,
    sovereign: buildSovereignStatus(),
  };
}

export function useBobState() {
  const [state, setState] = useState<BobState>(() => loadState() ?? defaultState());

  useEffect(() => {
    saveState(state);
  }, [state]);

  const setSurface = useCallback((activeSurface: SurfaceId) => {
    setState((s) => ({ ...s, activeSurface }));
  }, []);

  const patch = useCallback((partial: Partial<BobState>) => {
    setState((s) => ({ ...s, ...partial }));
  }, []);

  const togglePresentationMode = useCallback(() => {
    setState((s) => ({ ...s, presentationMode: !s.presentationMode }));
  }, []);

  const refreshSovereign = useCallback(() => {
    setState((s) => ({ ...s, sovereign: buildSovereignStatus(s.sovereign.adapters) }));
  }, []);

  const toggleAdapter = useCallback((id: string) => {
    setState((s) => ({
      ...s,
      sovereign: {
        ...s.sovereign,
        adapters: s.sovereign.adapters.map((a) =>
          a.id === id && a.mode === "optional-adapter" && a.id !== "claude-handoff"
            ? { ...a, enabled: !a.enabled }
            : a,
        ),
        checkedAt: new Date().toISOString(),
      },
    }));
  }, []);

  return {
    state,
    setState,
    setSurface,
    patch,
    togglePresentationMode,
    refreshSovereign,
    toggleAdapter,
  };
}
